
import { useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { WordPair } from "@/types/game";
import { debugLog, DebugLevel } from "@/utils/debugUtils";
import { processBothWords } from "@/utils/roundProcessing/wordProcessing";
import { generateGameStateSummary, createStateSetters } from "@/utils/roundProcessing/stateManagement";

export const useRoundProcessing = (
  gameId: string | null,
  rounds: WordPair[],
  currentWord: string,
  usedWords: Set<string>,
  setRounds: (rounds: WordPair[] | ((prev: WordPair[]) => WordPair[])) => void,
  setUsedWords: (usedWords: Set<string> | ((prev: Set<string>) => Set<string>)) => void,
  setCurrentWord: (word: string) => void,
  setIsRevealed: (isRevealed: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setBotIsThinking: (isThinking: boolean) => void,
  setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
  setGameOver: (gameOver: boolean) => void,
  setConvergenceRound: (round: number | null) => void,
  setGameStatus: (status: string) => void,
  setUserHasSubmitted: (hasSubmitted: boolean) => void,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setProcessingComplete: (complete: boolean) => void
) => {
  const { toast } = useToast();

  const handleProcessBothWords = useCallback(async (aiWord: string, aiExplanation: string | undefined) => {
    if (!gameId) {
      console.error("Cannot process both words: gameId missing");
      return;
    }
    
    const roundNumber = rounds.length + 1;
    debugLog(DebugLevel.INFO, "useRoundProcessing", `Processing round ${roundNumber} with: User word: ${currentWord}, Bot word: ${aiWord}`);
    
    // Generate debugging state summary
    await generateGameStateSummary(
      gameId,
      roundNumber,
      true, // botIsThinking
      true, // botHasChosenWord
      true, // userHasSubmitted
      false  // processingComplete
    );
    
    // Only set submitting state if not already submitted (avoids UI flashing)
    if (!currentWord.trim()) {
      setIsSubmitting(true);
    }
    
    // Create bundle of all state setters for cleaner function calls
    const stateSetters = createStateSetters(
      setRounds,
      setUsedWords,
      setCurrentWord,
      setIsRevealed,
      setBotHasChosenWord,
      setBotIsThinking,
      setShouldAutoFocus,
      setGameOver,
      setConvergenceRound,
      setGameStatus,
      setUserHasSubmitted,
      setProcessingComplete
    );
    
    // Process both words using the extracted utility function
    await processBothWords(
      gameId,
      roundNumber,
      currentWord,
      aiWord,
      aiExplanation,
      usedWords,
      setIsSubmitting,
      stateSetters,
      toast
    );
    
    debugLog(DebugLevel.INFO, "useRoundProcessing", "Round processing completed");
  }, [
    gameId,
    rounds.length,
    currentWord,
    usedWords,
    setRounds,
    setUsedWords,
    setCurrentWord,
    setIsRevealed,
    setBotHasChosenWord,
    setBotIsThinking,
    setShouldAutoFocus,
    setGameOver,
    setConvergenceRound,
    setGameStatus,
    setUserHasSubmitted,
    toast,
    setIsSubmitting,
    setProcessingComplete
  ]);

  return { handleProcessBothWords };
};
